import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Badge,
  Chip,
  Collapse,
  TablePagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { BiSearch, BiFilterAlt, BiRevision, BiX } from 'react-icons/bi';

// ─── Keyframes (injected once) ──────────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('datacard-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'datacard-spin-keyframes';
  style.textContent = `@keyframes datacard-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

// ─── Shadows (matching design system elevation scale) ───────────────────────

const SHADOW_LEVEL1 = '0px 1px 3px rgba(0,0,0,0.06), 0px 1px 2px rgba(0,0,0,0.04)';
const SHADOW_LEVEL2 = '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ActiveFilter {
  key: string;
  label: string;
  variant?: 'default' | 'warning';
  /** When true, chip has no delete icon and is not clearable */
  permanent?: boolean;
}

export interface ViewModeOption {
  value: string;
  icon: React.ReactElement;
  label?: string;
}

export interface DataCardProps {
  /** Current search value */
  search: string;
  /** Search change handler */
  onSearchChange: (value: string) => void;
  /** Search placeholder text */
  searchPlaceholder?: string;

  /** Current view mode key */
  viewMode?: string;
  /** View mode change handler */
  onViewModeChange?: (mode: string) => void;
  /** Available view modes */
  viewModes?: ViewModeOption[];

  /** Filter controls rendered inside the collapsible filter section */
  filterControls?: React.ReactNode;
  /** Controlled filter-panel visibility (omit for internal state) */
  filtersOpen?: boolean;
  /** Called when filter-panel visibility changes (required when filtersOpen is controlled) */
  onFiltersOpenChange?: (open: boolean) => void;
  /** Active filters from all sources (KPI cards, dropdowns, search, etc.) */
  activeFilters?: ActiveFilter[];
  /** Clear a single filter by key */
  onClearFilter?: (key: string) => void;
  /** Clear every active filter */
  onClearAllFilters?: () => void;

  /** Renders a refresh button in the toolbar */
  onRefresh?: () => void;
  /** Spins the refresh icon when true */
  loading?: boolean;

  /** Total row count for pagination */
  totalCount?: number;
  /** Current page (0-indexed) */
  page?: number;
  /** Rows per page */
  rowsPerPage?: number;
  /** Page change handler */
  onPageChange?: (event: unknown, newPage: number) => void;
  /** Rows-per-page change handler */
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Page size options */
  rowsPerPageOptions?: number[];
  /** Show the pagination footer */
  showPagination?: boolean;

  // ── Empty state ───────────────────────────────────────────────────────────

  /** Resource name for empty state (e.g. "Guest Rides") */
  resource?: string;
  /** Icon for the empty state badge */
  emptyIcon?: React.ReactElement;
  /** Whether the underlying dataset has any records before filtering.
   *  `false` → "zero data" variant. `true` / omitted → "no matches" variant. */
  hasData?: boolean;
  /** Override the empty-state title (default: "No {resource} yet" / "No {resource} match your filters") */
  emptyTitle?: string;
  /** Override the empty-state description copy */
  emptyDescription?: string;
  /** Primary CTA label for the empty state (e.g. "Request Ride") */
  emptyCtaLabel?: string;
  /** Primary CTA click handler */
  onEmptyCtaClick?: () => void;

  /** Optional sx for the Card container */
  sx?: React.ComponentProps<typeof Card>['sx'];
  /** When true, content area flexes to fill */
  fillHeight?: boolean;

  children: React.ReactNode;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function DataCard({
  search,
  onSearchChange,
  searchPlaceholder = 'Search\u2026',
  viewMode,
  onViewModeChange,
  viewModes,
  filterControls,
  filtersOpen: filtersOpenProp,
  onFiltersOpenChange,
  activeFilters = [],
  onClearFilter,
  onClearAllFilters,
  onRefresh,
  loading = false,
  totalCount = 0,
  page = 0,
  rowsPerPage = 25,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  showPagination = true,
  resource = 'items',
  emptyIcon,
  hasData = true,
  emptyTitle,
  emptyDescription,
  emptyCtaLabel,
  onEmptyCtaClick,
  sx,
  fillHeight = false,
  children,
}: DataCardProps) {
  const [filtersOpenInternal, setFiltersOpenInternal] = useState(false);
  const isControlled = filtersOpenProp !== undefined;
  const filtersOpen = isControlled ? filtersOpenProp : filtersOpenInternal;
  const toggleFilters = useCallback(() => {
    const next = !filtersOpen;
    if (isControlled) {
      onFiltersOpenChange?.(next);
    } else {
      setFiltersOpenInternal(next);
    }
  }, [filtersOpen, isControlled, onFiltersOpenChange]);

  const filterBadgeCount = activeFilters.filter(f => f.key !== 'search').length;
  const hasFilterControls = !!filterControls;

  const showEmptyState = !loading && totalCount === 0;
  const isFilteredEmpty = showEmptyState && hasData;

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '12px',
        boxShadow: SHADOW_LEVEL2,
        overflow: 'visible',
        ...sx,
      }}
    >
      {/* ── Toolbar: search (left) | view toggle + filters + refresh (right) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Search */}
        <TextField
          size='small'
          placeholder={searchPlaceholder}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <BiSearch style={{ fontSize: 18, color: 'inherit' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 260,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              bgcolor: 'grey.50',
            },
          }}
        />

        {/* Right-side controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* View mode toggle */}
          {viewModes && viewModes.length > 0 && onViewModeChange && (
            <Box
              sx={{
                display: 'flex',
                gap: '2px',
                bgcolor: 'grey.100',
                borderRadius: '8px',
                p: '2px',
              }}
            >
              {viewModes.map(mode => (
                <Tooltip key={mode.value} title={mode.label || mode.value}>
                  <IconButton
                    size='small'
                    onClick={() => onViewModeChange(mode.value)}
                    sx={{
                      borderRadius: '4px',
                      width: 32,
                      height: 32,
                      color: viewMode === mode.value ? 'text.primary' : 'grey.400',
                      bgcolor: viewMode === mode.value ? 'background.paper' : 'transparent',
                      boxShadow: viewMode === mode.value ? SHADOW_LEVEL1 : 'none',
                      '&:hover': {
                        bgcolor: viewMode === mode.value ? 'background.paper' : 'grey.200',
                      },
                    }}
                  >
                    {React.cloneElement(mode.icon as React.ReactElement<any>, {
                      size: 18,
                    })}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          )}

          {/* Filter toggle */}
          {hasFilterControls && (
            <Badge
              badgeContent={filterBadgeCount || undefined}
              color='primary'
              sx={{
                '& .MuiBadge-badge': {
                  fontWeight: 700,
                  fontSize: 11,
                  minWidth: 18,
                  height: 18,
                  transition: 'transform 200ms ease-in-out',
                },
              }}
            >
              <Button
                size='small'
                startIcon={<BiFilterAlt />}
                onClick={toggleFilters}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  bgcolor: filtersOpen ? 'grey.200' : 'grey.100',
                  color: 'grey.700',
                  fontWeight: 500,
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                Filters
              </Button>
            </Badge>
          )}

          {/* Refresh */}
          {onRefresh && (
            <Tooltip title='Refresh'>
              <IconButton size='small' onClick={onRefresh} disabled={loading} sx={{ color: 'grey.500' }}>
                <BiRevision
                  size={18}
                  style={
                    loading
                      ? { animation: 'datacard-spin 1s linear infinite' }
                      : undefined
                  }
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Collapsible filter controls ──────────────────────────────────── */}
      {hasFilterControls && (
        <Collapse in={filtersOpen}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              px: 3,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              alignItems: 'center',
            }}
          >
            {filterControls}
            {/* No clear button here: whenever a filter is active the chip row
                below already offers "Clear all", and admin's card has none. */}
          </Box>
        </Collapse>
      )}

      {/* ── Active filter chips ──────────────────────────────────────────── */}
      {activeFilters.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            px: 3,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            alignItems: 'center',
          }}
        >
          {activeFilters.map(f => {
            const isWarning = f.variant === 'warning';
            return (
              <Chip
                key={f.key}
                label={f.label}
                size='small'
                onDelete={onClearFilter && !f.permanent ? () => onClearFilter(f.key) : undefined}
                deleteIcon={<BiX style={{ fontSize: 14 }} />}
                sx={{
                  borderRadius: '4px',
                  bgcolor: isWarning ? 'warning.50' : 'grey.100',
                  color: isWarning ? 'warning.800' : 'grey.700',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: isWarning ? 'warning.400' : 'grey.400',
                    '&:hover': { color: isWarning ? 'warning.600' : 'grey.600' },
                  },
                }}
              />
            );
          })}
          {onClearAllFilters && (
            <Button
              variant='text'
              size='small'
              onClick={onClearAllFilters}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                color: 'grey.600',
                fontWeight: 500,
                minWidth: 'auto',
              }}
            >
              Clear all
            </Button>
          )}
        </Box>
      )}

      {/* ── Content / Empty State ────────────────────────────────────────── */}
      {showEmptyState ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          {emptyIcon && (
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                bgcolor: 'secondary.lighter',
                color: 'secondary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              {React.cloneElement(emptyIcon as React.ReactElement<any>, {
                size: 48,
              })}
            </Box>
          )}
          <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 1 }}>
            {emptyTitle ?? (isFilteredEmpty ? `No ${resource} match your filters` : `No ${resource} yet`)}
          </Typography>
          <Typography variant='body2' sx={{ color: 'grey.600', mb: 2 }}>
            {emptyDescription ??
              (isFilteredEmpty
                ? 'Try adjusting your search or filter criteria.'
                : `Get started by creating your first ${resource.toLowerCase()}.`)}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {emptyCtaLabel && onEmptyCtaClick && (
              <Button variant='contained' disableElevation onClick={onEmptyCtaClick} sx={{ textTransform: 'none' }}>
                {emptyCtaLabel}
              </Button>
            )}
            {/* Only offer to clear filters when there are filters to clear — an
                empty search with none active has nothing for this to do
                (matches admin's DataCard). */}
            {isFilteredEmpty && activeFilters.length > 0 && onClearAllFilters && (
              <Button
                variant='text'
                onClick={onClearAllFilters}
                sx={{ textTransform: 'none', color: 'grey.600', fontWeight: 500, fontSize: 13 }}
              >
                Clear all filters
              </Button>
            )}
          </Box>
        </Box>
      ) : fillHeight ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      ) : (
        children
      )}

      {/* ── Pagination footer ────────────────────────────────────────────── */}
      {showPagination && totalCount > 0 && onPageChange && onRowsPerPageChange && (
        <TablePagination
          component='div'
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: 12,
            },
          }}
        />
      )}
    </Card>
  );
}
